import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  CheckCircle, ExternalLink, Play, Phone, Calendar, 
  Bluetooth, Droplets, Battery, Volume2, Smartphone, Ear, X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

// Cochlear Products with MANUFACTURER videos
const cochlearProducts = [
  {
    id: "nucleus-8",
    name: "Nucleus 8 Sound Processor",
    model: "CP1110",
    price: "₹6,50,000 - ₹8,00,000",
    priceNote: "Includes implant, processor, surgery",
    image: "https://www.cochlear.com/content/dam/cochlear/shared-assets/global/nucleus-8/nucleus-8-sound-processor-champagne-front.png",
    tagline: "World's smallest behind-the-ear cochlear implant processor",
    features: [
      "15% smaller & 13% lighter than Nucleus 7",
      "SmartSound iQ 2 with SCAN 2 technology",
      "Bluetooth LE Audio & Auracast ready",
      "IP68 water & dust resistant",
      "Made for iPhone & Android",
      "Rechargeable & zinc-air battery options",
      "ForwardFocus with Beam technology",
      "6 color options available"
    ],
    specs: {
      "Water Resistance": "IP68 (3m for 2hrs with Aqua+)",
      "Battery Life": "Up to 24 hours (rechargeable)",
      "Connectivity": "Bluetooth LE, 2.4GHz wireless",
      "Weight": "8.2g (with battery)",
      "Compatibility": "CI600, CI500, CI24RE series"
    },
    bestFor: "Adults and children seeking the latest technology with smartphone connectivity",
    videoId: "bhcQDSTgDU4",
    videoTitle: "Introducing the Cochlear™ Nucleus® 8 Sound Processor",
    manufacturerUrl: "https://www.cochlear.com/us/en/home/products-and-accessories/cochlear-nucleus-system/nucleus-sound-processors/nucleus-8"
  },
  {
    id: "kanso-2",
    name: "Kanso 2 Sound Processor",
    model: "Off-the-ear design",
    price: "₹5,50,000 - ₹7,00,000",
    priceNote: "Single-unit off-ear processor",
    image: "https://www.cochlear.com/content/dam/cochlear/shared-assets/global/kanso-2/kanso2-sound-processor-sand.png",
    tagline: "Discreet, off-the-ear hearing with smart technology",
    features: [
      "Single-unit off-ear design - no ear hook",
      "SmartSound iQ with SCAN",
      "Made for iPhone streaming",
      "Rechargeable battery",
      "Water resistant IP54",
      "Easy to use - just snap on",
      "Ideal for active lifestyles",
      "Multiple color options"
    ],
    specs: {
      "Water Resistance": "IP54",
      "Battery Life": "Up to 16 hours",
      "Connectivity": "2.4GHz wireless, Bluetooth",
      "Weight": "13g",
      "Design": "Off-the-ear, single unit"
    },
    bestFor: "Those preferring a discreet, off-ear design with easy daily use",
    videoId: "4Jva-pkyvcI",
    videoTitle: "Cochlear™ Kanso® 2 Sound Processor",
    manufacturerUrl: "https://www.cochlear.com/us/en/home/products-and-accessories/cochlear-nucleus-system/nucleus-sound-processors/kanso-2"
  },
  {
    id: "osia-2",
    name: "Osia 2 System",
    model: "Bone Conduction",
    price: "₹5,00,000 - ₹6,50,000",
    priceNote: "For conductive/mixed hearing loss",
    image: "https://www.cochlear.com/content/dam/cochlear/shared-assets/global/osia-2/osia-2-sound-processor-polar-white.png",
    tagline: "Active bone conduction implant for exceptional hearing",
    features: [
      "Piezoelectric bone conduction",
      "Made for iPhone streaming",
      "SmartSound iQ technology",
      "Rechargeable battery",
      "Discreet behind-ear design",
      "For single-sided deafness or conductive loss",
      "Preserves natural ear canal",
      "Superior sound quality vs traditional BAHA"
    ],
    specs: {
      "Type": "Active bone conduction",
      "Battery Life": "Up to 16 hours",
      "Connectivity": "2.4GHz, Bluetooth",
      "Indication": "Conductive, mixed, SSD",
      "Surgery": "Minimally invasive"
    },
    bestFor: "Those with conductive hearing loss, mixed hearing loss, or single-sided deafness",
    videoId: "ZRRcBNGfZkk",
    videoTitle: "Cochlear™ Osia® System",
    manufacturerUrl: "https://www.cochlear.com/us/en/home/products-and-accessories/osia-system"
  }
];

// MED-EL Products with MANUFACTURER videos
const medelProducts = [
  {
    id: "rondo-3",
    name: "RONDO 3 Audio Processor",
    model: "Single-unit design",
    price: "₹6,00,000 - ₹7,50,000",
    priceNote: "With SYNCHRONY 2 implant",
    image: "https://blog.medel.com/wp-content/uploads/2020/12/RONDO-3_beige_front_RGB-scaled.jpg",
    tagline: "All-in-one design with wireless charging",
    features: [
      "Slim, lightweight single-unit (14.7g)",
      "Wireless charging - no cables needed",
      "Up to 24 hours battery life",
      "IP68 water resistant",
      "ASM 3.0 sound processing",
      "Dual microphones with Directionality",
      "Bluetooth streaming with AudioLink",
      "5+ year battery lifespan"
    ],
    specs: {
      "Water Resistance": "IP68 (1m for 60 min)",
      "Battery Life": "Up to 24 hours",
      "Charging": "Wireless inductive",
      "Weight": "14.7-15.9g",
      "Frequency Range": "70-9300 Hz"
    },
    bestFor: "Those wanting cable-free convenience with all-day wireless charging",
    videoId: "YeijLj1OMyY",
    videoTitle: "Unboxing RONDO 3 in Two Minutes - MED-EL",
    manufacturerUrl: "https://www.medel.com/hearing-solutions/cochlear-implants/rondo3"
  },
  {
    id: "sonnet-2",
    name: "SONNET 2 Audio Processor",
    model: "Behind-the-ear",
    price: "₹5,50,000 - ₹7,00,000",
    priceNote: "With SYNCHRONY implant",
    image: "https://www.medel.com/en-us/-/media/medel-main/images/audio-processors/sonnet-2/sonnet-2-silver-grey-rgb-right.png",
    tagline: "Ergonomic BTE with modular flexibility",
    features: [
      "Ergonomic behind-the-ear design",
      "ASM 3.0 Automatic Sound Management",
      "Waterproof AudioStream cover",
      "Direct streaming capable",
      "BabyWear compatible for infants",
      "Modular accessory system",
      "FineHearing technology",
      "Multiple wearing options"
    ],
    specs: {
      "Water Resistance": "Waterproof with cover",
      "Battery Life": "All day",
      "Design": "Behind-the-ear, modular",
      "Compatibility": "SYNCHRONY implants",
      "Technology": "ASM 3.0, FineHearing"
    },
    bestFor: "Families with children, those wanting traditional BTE style with latest tech",
    videoId: "2VvxQGT2LFI",
    videoTitle: "SONNET 3 Audio Processor - MED-EL",
    manufacturerUrl: "https://www.medel.com/hearing-solutions/cochlear-implants/sonnet2"
  },
  {
    id: "synchrony-implant",
    name: "SYNCHRONY 2 Cochlear Implant",
    model: "Internal implant",
    price: "Included in system price",
    priceNote: "MRI-safe up to 3.0T",
    image: "https://www.medel.com/en-us/-/media/medel-main/images/cochlear-implants/synchrony-2/mi1250-synchrony-2-ci-long-electrode.png",
    tagline: "MRI-safe implant with longest electrode coverage",
    features: [
      "MRI compatible up to 3.0T without magnet removal",
      "Longest electrode for complete cochlea coverage",
      "Sequential & parallel stimulation",
      "Up to 50,704 pulses per second",
      "24 current sources",
      "Future-proof for processor upgrades",
      "Soft, flexible electrode design",
      "Preserves residual hearing"
    ],
    specs: {
      "MRI Compatibility": "Up to 3.0 Tesla",
      "Electrodes": "12-31.5mm options",
      "Stimulation Rate": "Up to 50,704 pps",
      "Current Sources": "24",
      "Electrode Design": "Soft, atraumatic"
    },
    bestFor: "Those who may need future MRI scans or want the most flexible implant",
    videoId: "DIndZrbD0e0",
    videoTitle: "MED-EL SYNCHRONY Cochlear Implant",
    manufacturerUrl: "https://www.medel.com/hearing-solutions/cochlear-implants"
  }
];

// Advanced Bionics Products with MANUFACTURER videos
const abProducts = [
  {
    id: "naida-ci-m",
    name: "Naída CI Marvel",
    model: "For Adults",
    price: "₹6,50,000 - ₹8,00,000",
    priceNote: "With HiRes Ultra 3D implant",
    image: "https://www.advancedbionics.com/content/dam/advancedbionics/global/images/processors/marvel-ci/naida-ci-m-body-worn-gold-front.png",
    tagline: "Most connected cochlear implant with AI-powered sound",
    features: [
      "AutoSense OS 3.0 - auto-adjusts to environment",
      "Universal Bluetooth connectivity",
      "Connect to 9 devices simultaneously",
      "Hands-free calling & streaming",
      "HiRes Ultra sound quality",
      "T-Mic 2 for natural hearing",
      "Binaural VoiceStream technology",
      "Water resistant with accessories"
    ],
    specs: {
      "Connectivity": "Bluetooth Classic + LE",
      "Paired Devices": "Up to 9 simultaneous",
      "Battery Options": "Rechargeable & disposable",
      "Battery Life": "9-23 hours",
      "Technology": "AutoSense OS 3.0, HiRes"
    },
    bestFor: "Active adults who need extensive connectivity and hands-free calling",
    videoId: "U7feGu7xqcQ",
    videoTitle: "Marvel CI by Advanced Bionics",
    manufacturerUrl: "https://www.advancedbionics.com/us/en/home/explore/processors-and-cochlear-implants/naida-ci-m"
  },
  {
    id: "sky-ci-m",
    name: "Sky CI Marvel",
    model: "For Children",
    price: "₹6,50,000 - ₹8,00,000",
    priceNote: "Pediatric-specific design",
    image: "https://www.advancedbionics.com/content/dam/advancedbionics/global/images/processors/marvel-ci/sky-ci-m-body-worn-blue-front.png",
    tagline: "World's first cochlear implant designed specifically for children",
    features: [
      "AutoSense Sky 3.0 for children's environments",
      "Fun color options for kids",
      "Tamper-resistant battery door",
      "LED status indicator for parents",
      "RogerDirect compatibility for school",
      "Built tough for active kids",
      "Bilateral synchronization",
      "Easy parent monitoring via app"
    ],
    specs: {
      "Design": "Pediatric-specific",
      "Colors": "Multiple fun options",
      "Safety": "Tamper-resistant",
      "School Ready": "RogerDirect compatible",
      "Technology": "AutoSense Sky 3.0"
    },
    bestFor: "Children and families - specifically designed for pediatric needs",
    videoId: "0e2n8OV72a8",
    videoTitle: "Advanced Bionics Sky CI - Bluetooth streaming demo",
    manufacturerUrl: "https://www.advancedbionics.com/us/en/home/explore/processors-and-cochlear-implants/sky-ci-m",
    isPediatric: true
  },
  {
    id: "hires-ultra-3d",
    name: "HiRes Ultra 3D Implant",
    model: "Internal implant",
    price: "Included in system price",
    priceNote: "Most MRI-friendly implant",
    image: "https://www.advancedbionics.com/content/dam/advancedbionics/global/images/implants/hires-ultra-3d/hires-ultra-3d-implant.png",
    tagline: "No magnet removal needed for 3T MRI",
    features: [
      "3T MRI without magnet removal",
      "Self-aligning magnet technology",
      "HiFocus electrode arrays",
      "120 spectral bands",
      "82,492 pulses per second",
      "Thin, low-profile design",
      "ClearVoice speech enhancement",
      "Compatible with all Marvel processors"
    ],
    specs: {
      "MRI Compatibility": "3.0 Tesla (no removal)",
      "Spectral Bands": "120",
      "Stimulation Rate": "82,492 pps",
      "Magnet": "Self-aligning 3D",
      "Electrode": "HiFocus arrays"
    },
    bestFor: "Those who anticipate needing MRI scans or want maximum flexibility",
    videoId: "OuhqYmLrt6A",
    videoTitle: "Advanced Bionics HiRes Ultra 3D Implant",
    manufacturerUrl: "https://www.advancedbionics.com/us/en/home/explore/implants"
  }
];

const manufacturers = [
  { id: "cochlear", name: "Cochlear", country: "Australia", products: cochlearProducts, color: "bg-blue-600", logo: "🇦🇺" },
  { id: "medel", name: "MED-EL", country: "Austria", products: medelProducts, color: "bg-red-600", logo: "🇦🇹" },
  { id: "ab", name: "Advanced Bionics", country: "USA/Switzerland", products: abProducts, color: "bg-purple-600", logo: "🇺🇸" },
];

function ProductCard({ product }) {
  const [showVideo, setShowVideo] = useState(false);

  return (
    <Card className="overflow-hidden hover:shadow-lg transition-shadow">
      <div className="relative aspect-square bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-8">
        <img
          src={product.image}
          alt={product.name}
          className="max-h-full max-w-full object-contain"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = "https://via.placeholder.com/300x300?text=" + encodeURIComponent(product.name);
          }}
        />
        {product.isPediatric && (
          <Badge className="absolute top-4 left-4 bg-pink-600">For Children</Badge>
        )}
        {product.videoId && (
          <button
            onClick={() => setShowVideo(true)}
            className="absolute bottom-4 right-4 w-12 h-12 rounded-full bg-red-600 shadow-lg flex items-center justify-center hover:scale-110 transition-transform hover:bg-red-700"
            title="Watch Product Video"
          >
            <Play className="w-5 h-5 text-white ml-0.5" fill="currentColor" />
          </button>
        )}
      </div>
      
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-lg">{product.name}</CardTitle>
            <CardDescription>{product.model}</CardDescription>
          </div>
        </div>
        <p className="text-sm text-slate-600 mt-2">{product.tagline}</p>
      </CardHeader>
      
      <CardContent>
        <div className="mb-4">
          <div className="text-2xl font-bold text-sky-600">{product.price}</div>
          <div className="text-xs text-slate-500">{product.priceNote}</div>
        </div>

        <div className="space-y-2 mb-4">
          {product.features.slice(0, 4).map((feature, i) => (
            <div key={i} className="flex items-start gap-2 text-sm">
              <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span className="text-slate-600">{feature}</span>
            </div>
          ))}
        </div>

        <div className="p-3 bg-slate-50 rounded-lg mb-4">
          <div className="text-xs font-medium text-slate-500 mb-1">Best For:</div>
          <div className="text-sm text-slate-700">{product.bestFor}</div>
        </div>

        <div className="space-y-2">
          <Link to="/book-appointment">
            <Button className="w-full bg-amber-600 hover:bg-amber-700">
              Book CI Consultation
            </Button>
          </Link>
          {product.manufacturerUrl && (
            <a href={product.manufacturerUrl} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="w-full text-slate-600">
                View on Manufacturer Site
                <ExternalLink className="w-3 h-3 ml-2" />
              </Button>
            </a>
          )}
        </div>
      </CardContent>
      
      {/* Video Modal */}
      {showVideo && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setShowVideo(false)}>
          <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <button 
              onClick={() => setShowVideo(false)}
              className="absolute -top-12 right-0 text-white hover:text-slate-300"
            >
              <X className="w-8 h-8" />
            </button>
            <div className="text-white text-sm mb-2">{product.videoTitle}</div>
            <div className="aspect-video">
              <iframe
                src={`https://www.youtube.com/embed/${product.videoId}?autoplay=1&rel=0`}
                className="w-full h-full rounded-xl"
                allow="autoplay; encrypted-media"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

export default function CIProductsPage() {
  return (
    <div data-testid="ci-products-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-sky-50 to-white py-16 md:py-20">
        <div className="container-width text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Badge className="mb-4 bg-sky-100 text-sky-700">All Major Brands Available</Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Cochlear Implant Products
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-8">
              Dr. Sachdeva works with all three global CI manufacturers to recommend 
              the best solution for your specific hearing needs and lifestyle.
            </p>
            <div className="flex justify-center gap-4 flex-wrap">
              {manufacturers.map((mfr) => (
                <div key={mfr.id} className={`px-4 py-2 rounded-full ${mfr.color} text-white text-sm font-medium`}>
                  {mfr.logo} {mfr.name} ({mfr.country})
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quick Features */}
      <section className="bg-white py-8 border-b">
        <div className="container-width">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div className="p-4">
              <Bluetooth className="w-8 h-8 text-sky-600 mx-auto mb-2" />
              <div className="text-sm font-medium">Bluetooth Streaming</div>
              <div className="text-xs text-slate-500">All brands supported</div>
            </div>
            <div className="p-4">
              <Droplets className="w-8 h-8 text-sky-600 mx-auto mb-2" />
              <div className="text-sm font-medium">Water Resistant</div>
              <div className="text-xs text-slate-500">IP68 ratings available</div>
            </div>
            <div className="p-4">
              <Battery className="w-8 h-8 text-sky-600 mx-auto mb-2" />
              <div className="text-sm font-medium">All-Day Battery</div>
              <div className="text-xs text-slate-500">Rechargeable options</div>
            </div>
            <div className="p-4">
              <Smartphone className="w-8 h-8 text-sky-600 mx-auto mb-2" />
              <div className="text-sm font-medium">Smartphone Apps</div>
              <div className="text-xs text-slate-500">iOS & Android</div>
            </div>
          </div>
        </div>
      </section>

      {/* Products by Manufacturer */}
      <section className="section-padding bg-slate-50">
        <div className="container-width">
          <Tabs defaultValue="cochlear" className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-8 h-auto">
              {manufacturers.map((mfr) => (
                <TabsTrigger 
                  key={mfr.id} 
                  value={mfr.id} 
                  className="data-[state=active]:bg-sky-600 data-[state=active]:text-white py-3"
                >
                  <span className="mr-2">{mfr.logo}</span>
                  {mfr.name}
                </TabsTrigger>
              ))}
            </TabsList>

            {manufacturers.map((mfr) => (
              <TabsContent key={mfr.id} value={mfr.id}>
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-slate-900">{mfr.name} Cochlear Implants</h2>
                  <p className="text-slate-600">Products from {mfr.country} • Click play button to watch official product videos</p>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {mfr.products.map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* Price Note */}
      <section className="bg-amber-50 py-8">
        <div className="container-width text-center">
          <p className="text-amber-800">
            <strong>Note:</strong> Prices are indicative and may vary based on specific requirements. 
            Many government schemes (ADIP, state programs) and insurance policies cover cochlear implants. 
            Book a consultation to discuss your options and eligibility.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-slate-900 py-16">
        <div className="container-width text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Not Sure Which CI is Right for You?
          </h2>
          <p className="text-slate-300 mb-8 max-w-xl mx-auto">
            Dr. Sachdeva will evaluate your hearing loss, lifestyle, and preferences 
            to recommend the most suitable cochlear implant system.
          </p>
          <div className="flex justify-center gap-4 flex-wrap">
            <Link to="/book-appointment">
              <Button size="lg" className="bg-amber-600 hover:bg-amber-700">
                <Calendar className="w-5 h-5 mr-2" />
                Book Consultation
              </Button>
            </Link>
            <a href="tel:+919910317286">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                <Phone className="w-5 h-5 mr-2" />
                Call: 9910317286
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
